import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartItem } from '../../contexts/CartContext';
import { OrderDetails } from './CheckoutView';
import DeliveryMap from './DeliveryMap';
import { formatEtaRange } from '../../utils/routeUtils';
import { useOrders } from '../../contexts/OrdersContext';
import type { AdminOrderStatus } from '../../mocks/adminOrders';

interface PaymentPendingViewProps {
  orderId: string;
  total: number;
  items: CartItem[];
  details: OrderDetails;
  onClose: () => void;
}

const paymentLabels: Record<string, string> = {
  card: 'Credit / Debit Card',
  mobile: 'Mobile Money',
  cash: 'Cash on Delivery',
  wallet: 'SipDash Wallet',
};

const paymentIcons: Record<string, string> = {
  card: 'ri-bank-card-line',
  mobile: 'ri-smartphone-line',
  cash: 'ri-money-dollar-circle-line',
  wallet: 'ri-wallet-3-line',
};

type StatusKey = AdminOrderStatus | 'payment_pending';

const STEPS = [
  {
    label: 'Order Received',
    sub: 'Your order has been submitted',
    icon: 'ri-file-list-3-line',
  },
  {
    label: 'Payment Confirmed',
    sub: 'Our team approved your payment',
    icon: 'ri-shield-check-line',
  },
  {
    label: 'Preparing Your Order',
    sub: 'Being packed and prepared for expedition',
    icon: 'ri-store-2-line',
  },
  {
    label: 'Out for Delivery',
    sub: 'Your rider is on the way',
    icon: 'ri-motorbike-line',
  },
  {
    label: 'Delivered',
    sub: 'Enjoy your beverages!',
    icon: 'ri-checkbox-circle-line',
  },
];

function getActiveStep(status: StatusKey): number {
  switch (status) {
    case 'payment_pending': return 1;
    case 'pending': return 2;
    case 'processing': return 2;
    case 'on_way': return 3;
    case 'delivered': return 4;
    case 'cancelled': return -1;
    default: return 1;
  }
}

export default function PaymentPendingView({
  orderId,
  total,
  items,
  details,
  onClose,
}: PaymentPendingViewProps) {
  const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;
  const etaRange = formatEtaRange(details.address);

  const { orders } = useOrders();
  const order = orders.find((o) => o.id === orderId);
  const currentStatus = (order?.status ?? 'payment_pending') as StatusKey;
  const riderName = order?.rider ?? null;

  const [justConfirmed, setJustConfirmed] = useState(false);
  const prevStatus = useRef<StatusKey>('payment_pending');

  useEffect(() => {
    const s = currentStatus;
    if (prevStatus.current === 'payment_pending' && s !== 'payment_pending' && s !== 'cancelled') {
      setJustConfirmed(true);
      setTimeout(() => setJustConfirmed(false), 6000);
    }
    prevStatus.current = s;
  }, [currentStatus]);

  const activeStep = getActiveStep(currentStatus);
  const isCancelled = currentStatus === 'cancelled';
  const isPending = currentStatus === 'payment_pending';

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* ── Just Confirmed Banner ── */}
      {justConfirmed && (
        <div className="mx-4 mt-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-green-600 text-lg" />
          </div>
          <div>
            <p className="text-sm font-bold text-green-800">Order confirmed!</p>
            <p className="text-xs text-green-600 mt-0.5">Your payment was approved — we&apos;re preparing your order now.</p>
          </div>
        </div>
      )}

      {/* Status icon */}
      <div className="flex flex-col items-center justify-center px-6 pt-8 pb-5 text-center">
        <div className="relative mb-4">
          {isCancelled ? (
            <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-close-circle-line text-4xl text-red-500" />
              </div>
            </div>
          ) : isPending ? (
            <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-time-line text-4xl text-amber-500" />
              </div>
            </div>
          ) : activeStep >= 4 ? (
            <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-4xl text-green-500" />
              </div>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                <i className="ri-motorbike-line text-4xl text-orange-500" />
              </div>
            </div>
          )}
          {!isCancelled && (
            <div className="absolute -top-1 -right-1 w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 text-white">
              <i className="ri-secure-payment-line text-base" />
            </div>
          )}
        </div>

        <h2
          className="text-2xl font-black text-gray-900 mb-1"
          style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.04em' }}
        >
          {isCancelled
            ? 'Order Cancelled'
            : isPending
            ? 'Order Submitted!'
            : activeStep >= 4
            ? 'Delivered!'
            : activeStep === 3
            ? 'On The Way!'
            : 'Order Confirmed!'}
        </h2>
        <p className="text-gray-500 text-sm max-w-[280px] leading-relaxed">
          {isCancelled
            ? 'This order has been cancelled. Contact us if you have questions.'
            : isPending
            ? 'Awaiting payment confirmation. Once approved, your order will be sent for expedition.'
            : activeStep >= 4
            ? 'Your beverages have been delivered. Enjoy!'
            : activeStep === 3
            ? `Your rider${riderName ? ` ${riderName}` : ''} is heading your way!`
            : 'Your payment was confirmed. We\'re preparing your order now.'}
        </p>

        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex flex-col items-center gap-1">
          <span className="text-xs text-amber-600 font-semibold uppercase tracking-wide">Order ID</span>
          <span className="text-base font-black text-amber-700 tracking-wide">{orderId}</span>
        </div>

        {/* Live status indicator */}
        {!isCancelled && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-green-600 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
            Live tracking active
          </div>
        )}
      </div>

      {/* Live Status Steps */}
      {!isCancelled && (
        <div className="px-6 mb-5">
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Live Order Status</p>
              {isPending && (
                <span className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 font-semibold">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse inline-block" />
                  Awaiting confirmation
                </span>
              )}
              {!isPending && activeStep < 4 && (
                <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 border border-green-100 rounded-full px-2.5 py-1 font-semibold">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse inline-block" />
                  Live
                </span>
              )}
            </div>
            <div className="space-y-4">
              {STEPS.map((step, idx) => {
                const isDone = idx < activeStep;
                const isActive = idx === activeStep;
                return (
                  <div key={idx} className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isDone
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? isPending
                            ? 'bg-amber-400 text-white'
                            : 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {isDone ? (
                        <i className="ri-check-line text-sm" />
                      ) : (
                        <i className={`${step.icon} text-sm ${isActive ? 'animate-pulse' : ''}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${isDone || isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                        {step.label}
                        {idx === 3 && riderName && isActive && (
                          <span className="ml-1.5 text-xs font-normal text-orange-500">· {riderName}</span>
                        )}
                      </p>
                      <p className={`text-xs mt-0.5 ${isActive ? (isPending ? 'text-amber-500' : 'text-orange-500') : isDone ? 'text-gray-400' : 'text-gray-300'}`}>
                        {isActive ? `In progress...` : step.sub}
                      </p>
                    </div>
                    {isDone && (
                      <i className="ri-check-double-line text-green-500 text-sm mt-1 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Order summary */}
      <div className="px-6 mb-5">
        <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Order Summary</p>

          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {item.name}
                <span className="text-gray-400 ml-1">×{item.quantity}</span>
              </span>
              <span className="font-medium text-gray-800">{formatPrice(item.price * item.quantity)}</span>
            </div>
          ))}

          <div className="border-t border-gray-100 pt-2 flex justify-between font-bold text-gray-900">
            <span>Total</span>
            <span className="text-[#FF6B35]">{formatPrice(total)}</span>
          </div>

          {details.paymentMethod === 'cash' && details.changeAmount !== undefined && details.changeAmount >= 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3.5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                  <i className="ri-coins-line text-amber-600 text-sm" />
                </div>
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Seu Troco / Your Change</p>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-amber-700">
                    <i className="ri-arrow-right-s-line" />
                    <span>You pay: <strong>{formatPrice(details.cashAmount ?? 0)}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-amber-700">
                    <i className="ri-arrow-right-s-line" />
                    <span>Order total: <strong>{formatPrice(total)}</strong></span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-amber-600 font-medium mb-0.5">Rider brings back</p>
                  <p className="text-2xl font-black text-amber-700">{formatPrice(details.changeAmount)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-3 space-y-2">
            <div className="flex items-center gap-2">
              <i className={`${paymentIcons[details.paymentMethod]} text-sm text-gray-400`} />
              <span className="text-xs text-gray-500">{paymentLabels[details.paymentMethod]}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-map-pin-line text-sm text-gray-400 mt-0.5" />
              <span className="text-xs text-gray-500">{details.address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Google Maps route preview */}
      {details.address && !isCancelled && (
        <div className="px-6 mb-5">
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <div className="px-5 pt-4 pb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Delivery Route</p>
              <span className="text-xs font-bold text-[#FF6B35] bg-[#FFF5F0] px-2.5 py-1 rounded-full">
                ETA: {etaRange}
              </span>
            </div>
            <DeliveryMap destination={details.address} compact />
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-6 pb-6 space-y-3">
        <Link
          to={`/order/${orderId}`}
          onClick={onClose}
          className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-pin-time-line" />
          </div>
          View Full Tracking Page
        </Link>
        <button
          onClick={onClose}
          className="w-full border border-gray-200 text-gray-700 font-medium py-3 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
        >
          Continue Shopping
        </button>
        <p className="text-center text-xs text-gray-400">
          Order ID: <strong className="text-gray-600">{orderId}</strong>
        </p>
      </div>
    </div>
  );
}
